/**
 * Cómo se enseña la cola de espera (spec 029 · FR-003, FR-006, FR-007).
 *
 * El orden de la API es el de la cola (FIFO). Aquí no se reordena dentro
 * del día: al liberarse un jueves, gana quien es 1 de ese jueves.
 */
import { diaDeLaFecha, titular } from './tiempo'

export interface DeseoEspera {
    fecha: string | null
    horaMinima: string | null
    horaMaxima: string | null
}

/** La franja, sin el día. Si no acotó, «cualquier hora». */
export function describirFranja(a: Pick<DeseoEspera, 'horaMinima' | 'horaMaxima'>): string {
    if (a.horaMinima && a.horaMaxima) return `de ${a.horaMinima} a ${a.horaMaxima}`
    if (a.horaMinima) return `a partir de las ${a.horaMinima}`
    if (a.horaMaxima) return `hasta las ${a.horaMaxima}`
    return 'cualquier hora'
}

/** Qué quiere, en una frase. Sin jerga. */
export function describirDeseo(a: DeseoEspera): string {
    const franja = describirFranja(a)
    if (a.fecha === null) {
        return franja === 'cualquier hora' ? 'el primero que haya' : `el primero que haya, ${franja}`
    }
    const dia = diaDeLaFecha(a.fecha)
    return franja === 'cualquier hora' ? dia : `${dia}, ${franja}`
}

export interface GrupoEspera<T extends DeseoEspera> {
    clave: string
    titulo: string
    asientos: Array<T & { puesto: number }>
}

/**
 * Parte la cola por el día que esperan. Días en orden; «el primero»
 * al final. Dentro de cada grupo se conserva el FIFO.
 */
export function agruparPorDia<T extends DeseoEspera>(asientos: readonly T[]): GrupoEspera<T>[] {
    const grupos = new Map<string, T[]>()
    for (const a of asientos) {
        const clave = a.fecha ?? 'primero'
        const lista = grupos.get(clave) ?? []
        lista.push(a)
        grupos.set(clave, lista)
    }
    const claves = [...grupos.keys()].sort((a, b) => {
        if (a === 'primero') return 1
        if (b === 'primero') return -1
        return a.localeCompare(b)
    })
    return claves.map((clave) => {
        const lista = grupos.get(clave) ?? []
        return {
            clave,
            titulo: clave === 'primero' ? 'El primero que haya' : titular(diaDeLaFecha(clave)),
            asientos: lista.map((a, i) => ({ ...a, puesto: i + 1 })),
        }
    })
}
